import { Text, Image, Group, Badge, Anchor, Modal } from "@mantine/core";
import { formatDateTime } from "./date-format";
import { getCheckedChecklistItems } from "./checklist-utils";
import { FormFieldConfig, TemplateSubmissionField } from "@captiva/shared-types";

// Render a single field as a table row
import React, { useState } from "react";

export interface SubmissionFieldDisplayProps {
  label: string;
  value: any;
  type: string;
  metadata?: any;
}

function renderChecklistValue(value: unknown) {
  const checkedItems = getCheckedChecklistItems(value);

  if (checkedItems.length === 0) {
    return <Text color="dimmed">—</Text>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {checkedItems.map((item, idx) => (
        <Text key={`${item.label}-${idx}`} style={{ whiteSpace: "pre-wrap" }}>
          {item.label}
          {item.note !== undefined && item.note !== null && item.note !== "" ? ` - ${item.note}` : ""}
        </Text>
      ))}
    </div>
  );
}

function SubmissionFieldDisplay({ label, value, type, metadata }: SubmissionFieldDisplayProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalImg, setModalImg] = useState("");
  let displayValue = null;
  const handleImgClick = (imgUrl: string) => {
    setModalImg(imgUrl);
    setModalOpen(true);
  };
  if (value === undefined || value === null || value === "") {
    displayValue = <Text color="dimmed">—</Text>;
  } else {
    switch (type?.toLowerCase()) {
      case "boolean":
        displayValue = <Badge color={value ? "green" : "red"}>{value ? "True" : "False"}</Badge>;
        break;
      case "photo":
      case "signature":
        displayValue = value ? (
          <Image
            src={value}
            alt={label}
            width={120}
            radius="md"
            withPlaceholder
            style={{ cursor: "zoom-in" }}
            onClick={() => handleImgClick(value)}
          />
        ) : (
          <Text color="dimmed">No image</Text>
        );
        break;
      case "multiphoto":
        displayValue = Array.isArray(value) && value.length > 0 ? (
          <Group spacing={4}>
            {value.map((url, idx) => (
              <Image
                key={idx}
                src={url}
                alt={label + " " + (idx + 1)}
                width={80}
                radius="md"
                withPlaceholder
                style={{ cursor: "zoom-in" }}
                onClick={() => handleImgClick(url)}
              />
            ))}
          </Group>
        ) : (
          <Text color="dimmed">No images</Text>
        );
        break;
      case "gps":
        if (Array.isArray(value) && value.length === 2) {
          const [lon, lat] = value;
          displayValue = (
            <Anchor href={`https://www.google.com/maps?q=${lat},${lon}`} target="_blank" rel="noopener noreferrer">
              {lat}, {lon}
            </Anchor>
          );
        } else {
          displayValue = <Text color="dimmed">Invalid GPS</Text>;
        }
        break;
      case "date":
      case "datetime":
        const formatted = formatDateTime(value, type);
        displayValue = <Text color={formatted === "Invalid date/time" ? "dimmed" : undefined}>{formatted}</Text>;
        break;
      case "select":
        displayValue = <Text>{metadata?.label || value}</Text>;
        break;
      case "multiselect":
        displayValue = (
          <Text>
            {metadata && Array.isArray(metadata)
              ? metadata.map((m, idx) => (m && m.label ? m.label : String(value[idx]))).join(", ")
              : (Array.isArray(value) ? value.join(", ") : String(value))}
          </Text>
        );
        break;
      case "checklist":
      case "checklistwithnotes":
        displayValue = renderChecklistValue(value);
        break;
      case "number":
        displayValue = <Text>{value}</Text>;
        break;
      case "time":
      case "text":
      case "string":
      default:
        displayValue = <Text style={{ whiteSpace: "pre-wrap" }}>{String(value)}</Text>;
        break;
    }
  }
  return (
    <>
      <tr>
        <td style={{ fontWeight: 500, verticalAlign: "top", width: "30%" }}>{label}</td>
        <td>{displayValue}</td>
      </tr>
      <Modal opened={modalOpen} onClose={() => setModalOpen(false)} size="auto" centered withCloseButton>
        <Image src={modalImg} alt={label} radius="md" style={{ maxWidth: 600, maxHeight: 600, margin: "auto" }} />
      </Modal>
    </>
  );
}

// Render a single field value (for use in tables)
export interface FieldValueDisplayProps {
  label: string;
  value: any;
  type: string;
  metadata?: any;
}

export function FieldValueDisplay({ label, value, type, metadata }: FieldValueDisplayProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalImg, setModalImg] = useState("");
  let displayValue = null;
  const handleImgClick = (imgUrl: string) => {
    setModalImg(imgUrl);
    setModalOpen(true);
  };
  if (value === undefined || value === null || value === "") {
    displayValue = <Text color="dimmed">—</Text>;
  } else {
    switch (type?.toLowerCase()) {
      case "boolean":
        displayValue = <Badge color={value ? "green" : "red"}>{value ? "True" : "False"}</Badge>;
        break;
      case "photo":
      case "signature":
        displayValue = value ? (
          <Image
            src={value}
            alt={label}
            width={120}
            radius="md"
            withPlaceholder
            style={{ cursor: "zoom-in" }}
            onClick={() => handleImgClick(value)}
          />
        ) : (
          <Text color="dimmed">No image</Text>
        );
        break;
      case "multiphoto":
        displayValue = Array.isArray(value) && value.length > 0 ? (
          <Group spacing={4}>
            {value.map((url, idx) => (
              <Image
                key={idx}
                src={url}
                alt={label + " " + (idx + 1)}
                width={80}
                radius="md"
                withPlaceholder
                style={{ cursor: "zoom-in" }}
                onClick={() => handleImgClick(url)}
              />
            ))}
          </Group>
        ) : (
          <Text color="dimmed">No images</Text>
        );
        break;
      case "gps":
        if (Array.isArray(value) && value.length === 2) {
          const [lon, lat] = value;
          displayValue = (
            <Anchor href={`https://www.google.com/maps?q=${lat},${lon}`} target="_blank" rel="noopener noreferrer">
              {lat}, {lon}
            </Anchor>
          );
        } else {
          displayValue = <Text color="dimmed">Invalid GPS</Text>;
        }
        break;
      case "date":
      case "datetime":
        const formatted = formatDateTime(value, type);
        displayValue = <Text color={formatted === "Invalid date/time" ? "dimmed" : undefined}>{formatted}</Text>;
        break;
      case "select":
        displayValue = <Text>{metadata?.label || value}</Text>;
        break;
      case "multiselect":
        displayValue = (
          <Text>
            {metadata && Array.isArray(metadata)
              ? metadata.map((m, idx) => (m && m.label ? m.label : String(value[idx]))).join(", ")
              : (Array.isArray(value) ? value.join(", ") : String(value))}
          </Text>
        );
        break;
      case "checklist":
      case "checklistwithnotes":
        displayValue = renderChecklistValue(value);
        break;
      case "number":
        displayValue = <Text>{value}</Text>;
        break;
      case "time":
      case "text":
      case "string":
      default:
        displayValue = <Text style={{ whiteSpace: "pre-wrap" }}>{String(value)}</Text>;
        break;
    }
  }
  return (
    <>
      {displayValue}
      <Modal opened={modalOpen} onClose={() => setModalOpen(false)} size="auto" centered withCloseButton>
        <Image src={modalImg} alt={label} radius="md" style={{ maxWidth: 600, maxHeight: 600, margin: "auto" }} />
      </Modal>
    </>
  );
}

// Render all fields as a table
export interface SubmissionFieldDisplayTableProps {
  fields: FormFieldConfig[];
  data: Record<string, TemplateSubmissionField> | null | undefined;
}

export default function SubmissionFieldDisplayTable({ fields, data }: SubmissionFieldDisplayTableProps) {
  return (
      <>
        <table className="submission-field-table" style={{ width: "100%", borderCollapse: "collapse", border: '1px solid #e0e0e0' }} key={fields[0].key + 'h'}>
          <tbody>
            {fields.map((field, index) => (
              <React.Fragment key={field.key + index}>
                <SubmissionFieldDisplay
                  label={field.label}
                  value={data && data[field.key] ? data[field.key].value : ""}
                  type={field.type}
                  metadata={data && data[field.key] ? data[field.key].metadata : null}
                />
              </React.Fragment>
            ))}
          </tbody>
        </table>
        <style>{`
          .submission-field-table td, .submission-field-table th {
            border: 1px solid #e0e0e0;
            padding: 8px 12px;
          }
          .submission-field-table tr:not(:last-child) td {
            border-bottom: 1px solid #e0e0e0;
          }
          .submission-field-table tr td:first-child {
            background: #fafbfc;
            font-weight: 500;
            width: 30%;
          }
        `}</style>
        <style>{`
          table td, table th {
            border: 1px solid #e0e0e0;
            padding: 8px 12px;
          }
          table tr:not(:last-child) td {
            border-bottom: 1px solid #e0e0e0;
          }
          table tr td:first-child {
            background: #fafbfc;
            font-weight: 500;
            width: 30%;
          }
        `}</style>
      </>
  );
}
